// Primitive colors from ui-tokens
export const primitiveColors = {
  connectLightBlue: '#00b7f1',
  connectDarkBlue: '#000e4e',
  connectMediumBlue: '#0063cc',
  white: '#ffffff',
  black: '#000000',
  gray: {
    5: '#fafafa',
    10: '#f2f2f2',
    20: '#e6e6e6',
    30: '#cccccc',
    40: '#b3b3b3',
    50: '#999999',
    60: '#808080',
    70: '#666666',
    80: '#4d4d4d',
    90: '#333333',
    95: '#262626',
    100: '#1a1a1a',
    105: '#0d0d0d',
  },
  whiteAlpha: {
    10: '#ffffff19',
    12: '#ffffff1e',
    20: '#ffffff33',
    30: '#ffffff4c',
    40: '#ffffff66',
    50: '#ffffff7f',
    60: '#ffffff99',
    70: '#ffffffb2',
    80: '#ffffffcc',
    90: '#ffffffe5',
  },
  blackAlpha: {
    10: '#00000019',
    20: '#00000033',
    30: '#0000004c',
    40: '#00000066',
    50: '#0000007f',
    60: '#00000099',
    70: '#000000b2',
    80: '#000000cc',
    90: '#000000e5',
  },
  red: {
    10: '#ffe7e5',
    20: '#ffcfcc',
    30: '#ffa099',
    40: '#ff7066',
    50: '#ff4133',
    60: '#ff1100',
    70: '#cc0e00',
    80: '#990a00',
    90: '#660700',
    100: '#330300',
  },
  orange: {
    10: '#fff5e5',
    20: '#ffebcc',
    30: '#ffd699',
    40: '#ffc266',
    50: '#ffad33',
    60: '#ff9900',
    70: '#cc7a00',
    80: '#995c00',
    90: '#663d00',
    100: '#331f00',
  },
  yellow: {
    10: '#fffce5',
    20: '#fffacc',
    30: '#fff599',
    40: '#fff066',
    50: '#ffeb33',
    60: '#ffe500',
    70: '#ccb800',
    80: '#998a00',
    90: '#665c00',
    100: '#332e00',
  },
  green: {
    10: '#e5fff5',
    20: '#cffceb',
    30: '#99ffd8',
    40: '#66ffc4',
    50: '#33ffb1',
    60: '#00ff9d',
    70: '#00cc7e',
    80: '#00995e',
    90: '#00663f',
    100: '#00331f',
  },
  blue: {
    10: '#e5f2ff',
    20: '#cce5ff',
    30: '#99caff',
    40: '#66b0ff',
    50: '#3396ff',
    60: '#007bff',
    70: '#0063cc',
    80: '#004a99',
    90: '#003166',
    100: '#001933',
  },
  purple: {
    10: '#efe5ff',
    20: '#dfccff',
    30: '#be99ff',
    40: '#9e66ff',
    50: '#7e33ff',
    60: '#5e00ff',
    70: '#4b00cc',
    80: '#380099',
    90: '#250066',
    100: '#130033',
  },
};

// Light mode semantic colors
export const lightModeColors = {
  surface: {
    main: primitiveColors.connectMediumBlue,
    mainSubtleHovered: '#E9F2FB',
    mainSubtleSelected: '#A8CBF0',
    mainSubtleAlphaHovered: '#0063CC19',
    mainSubtleAlphaSelected: '#0063CC4C',
    mainHovered: '#004A99',
    mainSelected: '#003166',
    defaultSubtleSelected: '#E9F2FB',
    default: primitiveColors.white,
    defaultHovered: primitiveColors.gray[20],
    defaultPressed: primitiveColors.gray[30],
    defaultSelected: primitiveColors.gray[70],
    overlay: primitiveColors.white,
    defaultAlphaHovered: primitiveColors.blackAlpha[10],
    defaultAlphaPressed: primitiveColors.blackAlpha[20],
  },
  text: {
    main: primitiveColors.connectMediumBlue,
    inverted: primitiveColors.white,
    disabled: primitiveColors.blackAlpha[30],
    subtle: primitiveColors.blackAlpha[60],
    default: primitiveColors.blackAlpha[90],
  },
  border: {
    main: primitiveColors.connectMediumBlue,
    default: primitiveColors.blackAlpha[10],
    strong: primitiveColors.gray[60],
  },
  background: {
    default: primitiveColors.gray[10],
  },
  functionalColors: {
    error: {
      default: primitiveColors.red[70],
      hovered: primitiveColors.red[80],
      pressed: primitiveColors.red[90],
      subtle: primitiveColors.red[10],
      subtleHovered: primitiveColors.red[20],
      subtlePressed: primitiveColors.red[30],
    },
    warning: {
      yellow1: primitiveColors.yellow[50],
      yellow2: '#717A1F',
      orange1: '#F26C0D',
      orange2: primitiveColors.orange[80],
    },
    success: {
      1: primitiveColors.green[80],
      2: primitiveColors.green[90],
    },
    info: {
      1: primitiveColors.blue[60],
      2: '#195DE6',
    },
  },
  specific: {
    tableHeader: primitiveColors.gray[30],
    tableHeaderHovered: primitiveColors.gray[40],
    tableSortButton: primitiveColors.blackAlpha[30],
    tableSortButtonActive: primitiveColors.blackAlpha[90],
    globalNavigationBackground: primitiveColors.connectDarkBlue,
    globalNavigationSurfaceDefault: primitiveColors.white,
    globalNavigationSurfaceDefaultHovered: primitiveColors.whiteAlpha[10],
    globalNavigationSurfaceDefaultPressed: primitiveColors.whiteAlpha[30],
    globalNavigationSelector: primitiveColors.connectLightBlue,
    globalNavigationSurfaceDefaultSelected: '#00B7F166',
    headerLogo: primitiveColors.connectDarkBlue,
  },
  labels: {
    label01: '#E05B52',
    label02: '#E0A752',
    label03: '#E0D252',
    label04: '#1FAD77',
    label05: '#5281E0',
    label06: '#CB52E0',
    label07: primitiveColors.gray[40],
    label08: primitiveColors.gray[60],
  },
};

// Dark mode semantic colors
export const darkModeColors = {
  surface: {
    main: primitiveColors.connectLightBlue,
    mainSubtleHovered: '#1F3A47',
    mainSubtleSelected: '#176082',
    mainSubtleAlphaHovered: '#00B7F133',
    mainSubtleAlphaSelected: '#00B7F166',
    mainHovered: '#6ECCF7',
    mainSelected: '#9EDDFA',
    defaultSubtleSelected: '#1F3A47',
    default: primitiveColors.gray[100],
    defaultHovered: primitiveColors.gray[80],
    defaultPressed: primitiveColors.gray[70],
    defaultSelected: primitiveColors.gray[60],
    overlay: primitiveColors.gray[90],
    defaultAlphaHovered: primitiveColors.whiteAlpha[20],
    defaultAlphaPressed: primitiveColors.whiteAlpha[30],
  },
  text: {
    main: primitiveColors.connectLightBlue,
    inverted: primitiveColors.black,
    disabled: primitiveColors.whiteAlpha[30],
    subtle: primitiveColors.whiteAlpha[60],
    default: primitiveColors.whiteAlpha[90],
  },
  border: {
    main: primitiveColors.connectLightBlue,
    default: primitiveColors.whiteAlpha[12],
    strong: primitiveColors.gray[60],
  },
  background: {
    default: primitiveColors.black,
  },
  functionalColors: {
    error: {
      default: primitiveColors.red[50],
      hovered: primitiveColors.red[40],
      pressed: primitiveColors.red[30],
      subtle: primitiveColors.red[100],
      subtleHovered: primitiveColors.red[90],
      subtlePressed: primitiveColors.red[80],
    },
    warning: {
      yellow1: primitiveColors.yellow[50],
      yellow2: primitiveColors.yellow[50],
      orange1: primitiveColors.orange[60],
      orange2: primitiveColors.orange[60],
    },
    success: {
      1: primitiveColors.green[70],
      2: primitiveColors.green[70],
    },
    info: {
      1: primitiveColors.blue[60],
      2: primitiveColors.blue[60],
    },
  },
  specific: {
    tableHeader: primitiveColors.gray[90],
    tableHeaderHovered: primitiveColors.gray[80],
    tableSortButton: primitiveColors.whiteAlpha[30],
    tableSortButtonActive: primitiveColors.whiteAlpha[90],
    globalNavigationBackground: primitiveColors.gray[90],
    globalNavigationSurfaceDefault: primitiveColors.whiteAlpha[90],
    globalNavigationSurfaceDefaultHovered: primitiveColors.whiteAlpha[10],
    globalNavigationSurfaceDefaultPressed: primitiveColors.whiteAlpha[30],
    globalNavigationSelector: primitiveColors.connectLightBlue,
    globalNavigationSurfaceDefaultSelected: '#00B7F166',
    headerLogo: primitiveColors.white,
  },
  labels: {
    label01: '#E05B52',
    label02: '#E0A752',
    label03: '#E0D252',
    label04: '#1FAD77',
    label05: '#5281E0',
    label06: '#CB52E0',
    label07: primitiveColors.gray[40],
    label08: primitiveColors.gray[60],
  },
};

// Light theme configuration
export const lightThemeOptions = {
  palette: {
    type: 'light',
    primary: {
      main: lightModeColors.surface.main,
      light: lightModeColors.surface.mainHovered,
      dark: lightModeColors.surface.mainSelected,
      contrastText: lightModeColors.text.inverted,
    },
    secondary: {
      main: primitiveColors.white,
      light: primitiveColors.white,
      dark: primitiveColors.white,
      contrastText: lightModeColors.text.inverted,
    },
    error: {
      main: lightModeColors.functionalColors.error.default,
      light: lightModeColors.functionalColors.error.subtle,
      dark: lightModeColors.functionalColors.error.pressed,
    },
    warning: {
      main: lightModeColors.functionalColors.warning.orange1,
      light: lightModeColors.functionalColors.warning.yellow1,
      dark: lightModeColors.functionalColors.warning.orange2,
    },
    success: {
      main: lightModeColors.functionalColors.success[1],
      light: primitiveColors.green[60],
      dark: lightModeColors.functionalColors.success[2],
    },
    background: {
      default: lightModeColors.background.default,
      paper: lightModeColors.surface.default,
    },
    text: {
      primary: lightModeColors.text.default,
      secondary: lightModeColors.text.subtle,
      disabled: lightModeColors.text.disabled,
    },
    divider: lightModeColors.border.default,
  },
  navigation: {
    background: lightModeColors.specific.globalNavigationBackground,
    indicator: lightModeColors.specific.globalNavigationSelector,
    color: '#d5d6db',
    selectedColor: lightModeColors.specific.globalNavigationSurfaceDefault,
    navItem: {
      hoverBackground:
        lightModeColors.specific.globalNavigationSurfaceDefaultHovered,
    },
  },
};

// Dark theme configuration
export const darkThemeOptions = {
  palette: {
    type: 'dark',
    primary: {
      main: darkModeColors.surface.main,
      light: darkModeColors.surface.mainHovered,
      dark: darkModeColors.surface.mainSelected,
      contrastText: darkModeColors.text.inverted,
    },
    secondary: {
      main: primitiveColors.connectLightBlue,
      light: '#3396ff',
      dark: '#004a99',
      contrastText: darkModeColors.text.inverted,
    },
    error: {
      main: darkModeColors.functionalColors.error.default,
      light: darkModeColors.functionalColors.error.hovered,
      dark: darkModeColors.functionalColors.error.pressed,
    },
    warning: {
      main: darkModeColors.functionalColors.warning.orange1,
      light: darkModeColors.functionalColors.warning.yellow1,
      dark: darkModeColors.functionalColors.warning.orange2,
    },
    success: {
      main: darkModeColors.functionalColors.success[1],
      light: primitiveColors.green[60],
      dark: darkModeColors.functionalColors.success[2],
    },
    background: {
      default: darkModeColors.background.default,
      paper: darkModeColors.surface.default,
    },
    text: {
      primary: darkModeColors.text.default,
      secondary: darkModeColors.text.subtle,
      disabled: darkModeColors.text.disabled,
    },
    divider: darkModeColors.border.default,
  },
  navigation: {
    background: darkModeColors.specific.globalNavigationBackground,
    indicator: darkModeColors.specific.globalNavigationSelector,
    color: '#d5d6db',
    selectedColor: darkModeColors.specific.globalNavigationSurfaceDefault,
    navItem: {
      hoverBackground:
        darkModeColors.specific.globalNavigationSurfaceDefaultHovered,
    },
  },
};
